/**
 * SNAG Customizer Cart Features
 * - Quantity sync between main products and customization products
 * - Checkout blocking for upload customizations under minimum
 */

(function() {
  'use strict';

  if (window.SNAG_CART?.initialized) return;

  const SNAG_CART = {
    uploadMinimum: 12,
    qualifyingTags: ['Custom Headwear', 'Custom Apparel'],
    setupFeeProductVariant: '47312516284655',
    quantityBreaks: [1, 2, 12, 24, 48, 72, 144],
    cartDrawerHiddenQuantityBreaks: [2],
    customizationQuantityPricingEndpoint: 'https://snagcustomizer.com/api/v1/settings/customization_quantity_pricing',
    customizationQuantityPricingCacheTtlMs: 10 * 60 * 1000,
    customizationQuantityPricingByVariantId: {},
    customizationQuantityPricingByProductKey: {},
    fallbackEmbroideryQuantityTargetPrices: {
      front: {
        variantIds: ['47312008872175', '47311970337007'],
        pricesByBreakpoint: {
          12: 1495,
          24: 895,
          48: 595,
          72: 495,
          144: 395,
        },
      },
      sideBack: {
        variantIds: ['47312009003247', '47312008937711', '47312008970479'],
        pricesByBreakpoint: {
          12: 495,
          24: 450,
          48: 425,
          72: 395,
          144: 295,
        },
      },
    },
    embroideryCustomizationTypes: ['Text_Design', 'Uploaded_Image', 'Design_Image', 'Design_Product'],
    isProcessingSetupFees: false,
    isSyncingLinkedQuantities: false,
    isApplyingQuantityBreak: false,
    isRefreshPending: false,
    initialized: false,
    _lastBlockedState: undefined,
    _cartFetchPromise: null,
    _quantityPricingFetchPromise: null,
    _deferredCartChange: null,
    _linkedSyncTimeout: null,
    debug: true,

    log(...args) {
      if (this.debug) console.log('[SNAG Debug]', ...args);
    },

    init() {
      if (this.initialized) return;
      this.initialized = true;

      this.log('SNAG_CART initializing...');
      this.log('Config:', {
        uploadMinimum: this.uploadMinimum,
        qualifyingTags: this.qualifyingTags,
        setupFeeProductVariant: this.setupFeeProductVariant
      });
      this.hydrateCustomizationQuantityPricingFromCache();
      this.bindEvents();
      this.initCustomizationPreviewModal();
      this.checkCheckoutStatus();
      this.observeCartDrawer();
      this.syncCartDrawerCountLabel();
      this.groupLinkedCartItems();
      this.renderCustomizerQuantityBreaks();
      this.loadCustomizationQuantityPricing();
      this.debouncedSyncLinkedQuantities(0);
      this.manageSetupFees();
      this.log('SNAG_CART initialized');
    },

    getShopDomain() {
      return window.Shopify?.shop || window.Shopify?.shopName || '';
    },

    getQuantityPricingCacheKey() {
      const shopDomain = this.getShopDomain();
      return shopDomain ? `snag-customization-quantity-pricing:${shopDomain}` : '';
    },

    hydrateCustomizationQuantityPricingFromCache() {
      const cacheKey = this.getQuantityPricingCacheKey();
      if (!cacheKey) return;

      try {
        const cached = JSON.parse(window.localStorage.getItem(cacheKey) || 'null');
        if (!cached || Date.now() - cached.cachedAt > this.customizationQuantityPricingCacheTtlMs) return;

        this.applyCustomizationQuantityPricingPayload(cached.payload);
      } catch (error) {
        this.log('Ignoring invalid customization quantity pricing cache', error);
      }
    },

    async loadCustomizationQuantityPricing() {
      if (this._quantityPricingFetchPromise) return this._quantityPricingFetchPromise;

      const shopDomain = this.getShopDomain();
      if (!shopDomain) return null;

      const requestUrl = `${this.customizationQuantityPricingEndpoint}?shop_url=${encodeURIComponent(shopDomain)}`;
      this._quantityPricingFetchPromise = this.fetchJsonWithTimeout(requestUrl, 5000)
        .then((payload) => {
          this.applyCustomizationQuantityPricingPayload(payload);
          const cacheKey = this.getQuantityPricingCacheKey();
          if (cacheKey) {
            try {
              window.localStorage.setItem(cacheKey, JSON.stringify({
                cachedAt: Date.now(),
                payload,
              }));
            } catch (error) {
              this.log('Unable to cache customization quantity pricing', error);
            }
          }
          this.renderCustomizerQuantityBreaks();
          return payload;
        })
        .catch((error) => {
          this.log('Unable to load customization quantity pricing; using fallback curve', error);
          return null;
        })
        .finally(() => {
          this._quantityPricingFetchPromise = null;
        });

      return this._quantityPricingFetchPromise;
    },

    async fetchJsonWithTimeout(url, timeoutMs) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), timeoutMs);

      try {
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'omit',
          signal: controller.signal,
          headers: {
            Accept: 'application/json',
          },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
      } finally {
        clearTimeout(timeout);
      }
    },

    applyCustomizationQuantityPricingPayload(payload) {
      const normalized = this.normalizeCustomizationQuantityPricing(payload);
      this.customizationQuantityPricingByVariantId = normalized.variants;
      this.customizationQuantityPricingByProductKey = normalized.products;
    },

    normalizeCustomizationQuantityPricing(payload) {
      const variants = payload?.variants || {};
      const products = payload?.products || {};

      const normalizeVariantMap = (variantMap = {}) => Object.entries(variantMap).reduce((acc, [variantId, config]) => {
        const pricesByBreakpoint = {};
        (config?.quantity_breaks || []).forEach((priceBreak) => {
          const minimumQuantity = parseInt(priceBreak.minimum_quantity, 10);
          const unitPriceCents = this.parseCents(priceBreak.unit_price_cents);
          if (!Number.isFinite(minimumQuantity) || unitPriceCents == null) return;

          pricesByBreakpoint[minimumQuantity] = unitPriceCents;
        });

        if (Object.keys(pricesByBreakpoint).length > 0) {
          acc[String(variantId)] = pricesByBreakpoint;
        }

        return acc;
      }, {});

      return {
        variants: normalizeVariantMap(variants),
        products: Object.entries(products).reduce((acc, [productKey, productConfig]) => {
          const normalizedVariants = normalizeVariantMap(productConfig?.variants || {});
          if (Object.keys(normalizedVariants).length > 0) {
            acc[String(productKey)] = normalizedVariants;
          }
          if (productConfig?.handle) {
            acc[String(productConfig.handle)] = normalizedVariants;
          }
          if (productConfig?.product_id) {
            acc[String(productConfig.product_id)] = normalizedVariants;
          }
          return acc;
        }, {}),
      };
    },

    observeCartDrawer() {
      // Watch for DOM changes in cart drawer to catch AJAX updates
      let cartDrawerObserverTimeout = null;

      const setupObserver = () => {
        const cartDrawer = document.querySelector('#cart-drawer, [data-cart-drawer], .drawer--cart');
        if (cartDrawer && !cartDrawer._snagObserving) {
          cartDrawer._snagObserving = true;
          const observer = new MutationObserver(() => {
            // Debounce to batch rapid DOM changes from theme cart rebuild
            if (cartDrawerObserverTimeout) clearTimeout(cartDrawerObserverTimeout);
            cartDrawerObserverTimeout = setTimeout(() => {
              this._lastBlockedState = undefined;
              this.checkCheckoutStatus();
              this.syncCartDrawerCountLabel();
              this.groupLinkedCartItems();
              this.renderCustomizerQuantityBreaks();
              this.debouncedSyncLinkedQuantities();
              // Only trigger manageSetupFees if not already processing (prevents re-entrancy)
              if (!this.isProcessingSetupFees) {
                this.debouncedManageSetupFees();
              }
            }, 300);
          });
          observer.observe(cartDrawer, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-upload-blocked', 'data-setup-fee-variant-ids', 'data-qualifying-quantity']
          });
          return true;
        }
        return !!cartDrawer;
      };

      // Try immediately
      if (!setupObserver()) {
        // Cart drawer not found yet — watch for it to appear, then disconnect
        const observeTarget = document.querySelector('[data-site-container]') || document.body;
        const bodyObserver = new MutationObserver(() => {
          if (setupObserver()) {
            bodyObserver.disconnect();
          }
        });
        bodyObserver.observe(observeTarget, { childList: true, subtree: true });
      }
    },

    bindEvents() {
      // Listen for cart updates
      document.addEventListener('theme:cart:change', () => this.onCartChange());
      document.addEventListener('theme:cart:refresh', () => this.onCartChange());
      document.addEventListener('theme:product:added', () => this.onCartChange());

      document.addEventListener('click', (e) => {
        const closeButton = e.target.closest('[data-cart-drawer-close]');
        if (!closeButton) return;

        const cartDrawer = closeButton.closest('cart-drawer');
        if (!cartDrawer) return;

        const primaryCloseButton = cartDrawer.querySelector('[data-cart-drawer-close]');
        if (closeButton === primaryCloseButton) return;

        e.preventDefault();
        e.stopPropagation();

        if (typeof cartDrawer.closeCartDrawer === 'function') {
          cartDrawer.closeCartDrawer();
        }
      });

      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-snag-fees-toggle]');
        if (!toggle) return;

        e.preventDefault();
        const container = toggle.closest('.cart__linked-items');
        if (!container) return;

        const expanded = !container.classList.contains('is-expanded');
        container.classList.toggle('is-expanded', expanded);
        container.dataset.expanded = expanded ? 'true' : 'false';
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        const label = toggle.querySelector('[data-snag-fees-toggle-label]');
        if (label) label.textContent = expanded ? 'Hide fees' : 'Show fees details';
      });

      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-snag-design-details-toggle]');
        if (!toggle) return;

        e.preventDefault();
        e.stopPropagation();
        this.toggleInlineDesignDetails(toggle);
      });

      // Intercept only SNAG customization removals so normal products keep the theme's native drawer behavior.
      document.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-item-remove], [data-remove-item], .cart__remove, a[href*="/cart/change"]');
        if (!removeBtn) return;

        if (removeBtn.classList.contains('is-disabled') || removeBtn.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        const cartItem = removeBtn.closest('.cart__item[data-item]');
        if (!this.isSnagCustomizationCartItemElement(cartItem)) return;

        e.preventDefault();
        e.stopPropagation();
        this.handleRemoveItem(removeBtn);
      }, true);

      // Open customization preview popup
      document.addEventListener('click', (e) => {
        const viewCustomizationBtn = e.target.closest('[data-view-customization]');
        if (!viewCustomizationBtn) return;

        e.preventDefault();
        e.stopPropagation();
        this.openCustomizationPreview(viewCustomizationBtn.dataset.imageUrl);
      });

      // Close customization preview popup
      document.addEventListener('click', (e) => {
        const closeTrigger = e.target.closest('[data-snag-customization-close]');
        if (!closeTrigger) return;

        e.preventDefault();
        this.closeCustomizationPreview();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeCustomizationPreview();
        }
      });

      document.addEventListener('click', (e) => {
        const quantityBreakButton = e.target.closest('[data-snag-quantity-break]');
        if (!quantityBreakButton) return;

        e.preventDefault();
        e.stopPropagation();
        this.handleQuantityBreakSelection(quantityBreakButton);
      });

      // Block checkout form submission if upload minimum not met
      document.addEventListener('submit', (e) => {
        if (e.target.matches('#cartForm, [data-cart-form]')) {
          if (this.isCheckoutBlocked()) {
            e.preventDefault();
            e.stopPropagation();
            this.showUploadWarning();
            return false;
          }
        }
      });

      // Block checkout button clicks
      document.addEventListener('click', (e) => {
        const checkoutBtn = e.target.closest('button[name="checkout"], [data-cart-checkout-button] button');
        if (checkoutBtn && this.isCheckoutBlocked()) {
          e.preventDefault();
          e.stopPropagation();
          this.showUploadWarning();
          return false;
        }
      });
    },

    async handleRemoveItem(removeBtn) {
      this.setRemoveButtonState(removeBtn, true);

      // Get item key from the remove button
      let itemKey = removeBtn.dataset.id || removeBtn.dataset.itemKey;

      // Try to get from href if not in data attributes
      if (!itemKey) {
        const href = removeBtn.getAttribute('href') || '';
        const match = href.match(/line=(\d+)/) || href.match(/id=([^&]+)/);
        if (match) {
          itemKey = match[1];
        }
      }

      // Try to get from parent cart item element
      if (!itemKey) {
        const cartItem = removeBtn.closest('[data-item-key], [data-id], .cart__item');
        if (cartItem) {
          itemKey = cartItem.dataset.itemKey || cartItem.dataset.id;
        }
      }

      if (!itemKey) {
        console.warn('SNAG Cart: Could not find item key for remove button');
        this.setRemoveButtonState(removeBtn, false);
        return;
      }

      try {
        const cart = await this.getCart();
        const removedItem = cart.items.find(item => item.key === itemKey || String(item.id) === itemKey);

        if (!removedItem) {
          // Item might be identified by line number
          const lineIndex = parseInt(itemKey, 10) - 1;
          if (lineIndex >= 0 && lineIndex < cart.items.length) {
            const itemByLine = cart.items[lineIndex];
            const removal = await this.removeLinkedItems(itemByLine, cart);
            if (removal.ok) {
              this.removeCartItemsFromDom(removal.removedKeys);
              this.updateCartSummaryFromCart(removal.cart);
              this.flushPendingRefresh({ removedKeys: removal.removedKeys });
            } else {
              this.setRemoveButtonState(removeBtn, false);
            }
            return;
          }
          console.warn('SNAG Cart: Could not find item in cart');
          this.setRemoveButtonState(removeBtn, false);
          return;
        }

        const removal = await this.removeLinkedItems(removedItem, cart);
        if (removal.ok) {
          this.removeCartItemsFromDom(removal.removedKeys);
          this.updateCartSummaryFromCart(removal.cart);
          this.flushPendingRefresh({ removedKeys: removal.removedKeys });
        } else {
          this.setRemoveButtonState(removeBtn, false);
        }
      } catch (error) {
        console.error('SNAG Cart: Error removing linked items', error);
        this.setRemoveButtonState(removeBtn, false);
      }
    },

    async removeLinkedItems(item, cart) {
      const associationKey = this.getRemovalAssociationKeyForCartItem(item);
      const itemsWithLines = cart.items.map((cartItem, index) => ({
        cartItem,
        line: index + 1,
      }));

      const itemsToRemove = associationKey
        ? itemsWithLines.filter(({ cartItem }) => this.getRemovalAssociationKeyForCartItem(cartItem) === associationKey)
        : itemsWithLines.filter(({ cartItem }) => cartItem.key === item.key);

      if (!itemsToRemove.length) {
        return { ok: false, removedKeys: [], cart: null };
      }

      const removedKeys = itemsToRemove.map(({ cartItem }) => cartItem.key);
      const sortedItemsToRemove = [...itemsToRemove].sort((left, right) => right.line - left.line);
      let cartAfterUpdate = null;

      console.log('SNAG Cart: Removing linked items', {
        associationKey,
        count: sortedItemsToRemove.length,
        lines: sortedItemsToRemove.map(({ line }) => line),
        removedKeys,
      });

      for (const { line } of sortedItemsToRemove) {
        cartAfterUpdate = await this.changeCartLine(line, 0);
        if (!cartAfterUpdate) {
          return { ok: false, removedKeys, cart: null };
        }
      }

      return { ok: true, removedKeys, cart: cartAfterUpdate };
    },

    async changeCartLine(line, quantity) {
      try {
        const cartChangeUrl = window.theme?.routes?.cart_change_url || '/cart/change.js';
        const response = await fetch(cartChangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity }),
        });

        const text = await response.text();
        let parsedState = null;

        try {
          parsedState = JSON.parse(text);
        } catch (error) {
          console.warn('SNAG Cart: Cart change returned non-JSON response', text.substring(0, 160));
          return null;
        }

        if (!response.ok || parsedState.errors) {
          console.warn('SNAG Cart: Cart change failed', {
            line,
            quantity,
            status: response.status,
            errors: parsedState.errors,
          });
          return null;
        }

        this._cartFetchPromise = null;
        this.isRefreshPending = true;
        return parsedState;
      } catch (error) {
        console.error('SNAG Cart: Error changing cart line', error);
      }
      return null;
    },

    async updateCartItems(updates, options = {}) {
      try {
        const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';
        const response = await fetch(cartUpdateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });

        if (response.ok) {
          this._cartFetchPromise = null;
          if (!options.silent) {
            this.isRefreshPending = true;
          }
          try {
            return await response.json();
          } catch (error) {
            return null;
          }
        }
        console.warn('SNAG Cart: Cart update failed', response.status, response.statusText);
      } catch (error) {
        console.error('SNAG Cart: Error updating cart', error);
      }
      return null;
    },

    flushPendingRefresh(options = {}) {
      if (this.isRefreshPending) {
        this.isRefreshPending = false;
        this.refreshCartUi(options).catch((error) => {
          console.error('SNAG Cart: Error flushing cart UI refresh', error);
        });
      }
    },

    async refreshCartUi(options = {}) {
      const removedKeys = options.removedKeys || [];
      const refreshedFromSection = await this.refreshCartUiFromSection({ removedKeys });
      if (refreshedFromSection) return;

      // For custom removals, never fall back to the theme's unvalidated refresh.
      // A stale section is exactly what makes removed customization rows reappear.
      if (removedKeys.length > 0) return;

      const refreshedByTheme = Array.from(document.querySelectorAll('cart-items')).reduce((refreshed, cartItem) => {
        if (typeof cartItem.getCart !== 'function') return refreshed;

        cartItem.getCart();
        return true;
      }, false);

      if (!refreshedByTheme) {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      }
    },

    async refreshCartUiFromSection(options = {}) {
      const removedKeys = options.removedKeys || [];
      const cartItems = Array.from(document.querySelectorAll('cart-items'));
      const cartItemBuilder = cartItems.find((cartItem) => typeof cartItem.build === 'function');
      if (!cartItemBuilder) return false;

      const cartUrl = window.theme?.routes?.cart_url || '/cart';
      const maxAttempts = removedKeys.length > 0 ? 6 : 1;

      try {
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const sectionUrl = new URL(cartUrl, window.location.origin);
          sectionUrl.searchParams.set('section_id', 'api-cart-items');
          sectionUrl.searchParams.set('_snag_refresh', `${Date.now()}-${attempt}`);

          const response = await fetch(sectionUrl.toString(), {
            cache: 'no-store',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          });
          if (!response.ok) return false;

          const html = await response.text();
          const element = document.createElement('div');
          element.innerHTML = html;

          const cleanResponse = element.querySelector('[data-api-content]');
          if (!cleanResponse) return false;

          if (this.cartSectionContainsItemKeys(cleanResponse, removedKeys)) {
            await this.waitForCartRefreshRetry(attempt);
            continue;
          }

          cartItemBuilder.build(cleanResponse);
          this._lastBlockedState = undefined;
          this.syncCartDrawerCountLabel();
          this.groupLinkedCartItems();
          this.renderCustomizerQuantityBreaks();
          this.checkCheckoutStatus();

          return true;
        }

        console.warn('SNAG Cart: Skipped stale cart section refresh after removal', removedKeys);
      } catch (error) {
        console.error('SNAG Cart: Error refreshing cart UI', error);
      }
      return false;
    },

    cartSectionContainsItemKeys(sectionElement, itemKeys = []) {
      const expectedKeys = new Set(itemKeys.filter(Boolean));
      if (expectedKeys.size === 0) return false;

      return Array.from(sectionElement.querySelectorAll('[data-item]')).some((cartItem) => {
        return expectedKeys.has(cartItem.dataset.item);
      });
    },

    waitForCartRefreshRetry(attempt) {
      const delays = [150, 250, 400, 650, 1000];
      const delay = delays[Math.min(attempt, delays.length - 1)];
      return new Promise((resolve) => setTimeout(resolve, delay));
    },

    removeCartItemsFromDom(itemKeys = []) {
      const expectedKeys = new Set(itemKeys.filter(Boolean));
      if (expectedKeys.size === 0) return;

      document.querySelectorAll('.cart__item[data-item]').forEach((cartItem) => {
        if (expectedKeys.has(cartItem.dataset.item)) {
          cartItem.remove();
        }
      });
    },

    updateCartSummaryFromCart(cart) {
      if (!cart) return;

      const visibleCount = this.getVisibleCartCountFromCart(cart);
      document.dispatchEvent(
        new CustomEvent('theme:cart:change', {
          bubbles: true,
          detail: {
            cartCount: visibleCount,
          },
        })
      );

      document.querySelectorAll('[data-cart-total]').forEach((total) => {
        total.setAttribute('data-cart-total', cart.total_price || 0);
        total.innerHTML = this.formatCartMoney(cart.total_price || 0);
      });
    },

    getVisibleCartCountFromCart(cart) {
      if (!cart?.items) return 0;

      return cart.items.reduce((total, item) => {
        const isLinkedCustomization = !!this.getProperty(item, '_parentVariantId');
        const isSetupFee = String(item.variant_id) === this.setupFeeProductVariant || !!this.getProperty(item, '_setupFeeForVariant');
        if (isLinkedCustomization || isSetupFee) return total;

        return total + (parseInt(item.quantity, 10) || 0);
      }, 0);
    },

    formatCartMoney(cents) {
      if (cents === 0) return window.theme?.strings?.free || 'Free';

      if (window.theme?.formatMoney) {
        const moneyFormat = window.theme.moneyWithCurrencyFormat || window.theme.moneyFormat || '${{amount}}';
        return window.theme.formatMoney(cents, moneyFormat);
      }

      return `$${(cents / 100).toFixed(2)}`;
    },

    setRemoveButtonState(removeBtn, isRemoving) {
      if (!removeBtn) return;

      const cartItem = removeBtn.closest('.cart__item[data-item]');
      if (cartItem) {
        cartItem.classList.toggle('is-loading', isRemoving);
        cartItem.classList.toggle('is-removed', isRemoving);
      }

      if (isRemoving) {
        removeBtn.setAttribute('aria-disabled', 'true');
        removeBtn.classList.add('is-disabled');
      } else {
        removeBtn.removeAttribute('aria-disabled');
        removeBtn.classList.remove('is-disabled');
      }
    },

    debouncedSyncLinkedQuantities(delay = 250) {
      if (this._linkedSyncTimeout) {
        clearTimeout(this._linkedSyncTimeout);
      }

      this._linkedSyncTimeout = setTimeout(() => {
        this.syncAllLinkedQuantities();
      }, delay);
    },

    async syncAllLinkedQuantities() {
      if (this.isProcessingSetupFees) {
        this.log('syncAllLinkedQuantities: Skipped while setup fees are processing');
        return;
      }
      if (this.isSyncingLinkedQuantities) return;
      this.isSyncingLinkedQuantities = true;

      try {
        const cart = await this.getCart();
        const groupsByAssociation = new Map();

        cart.items.forEach((item) => {
          const associationKey = this.getAssociationKeyForCartItem(item);
          if (!associationKey) return;

          if (!groupsByAssociation.has(associationKey)) {
            groupsByAssociation.set(associationKey, { parentItem: null, linkedFeeItems: [] });
          }

          const group = groupsByAssociation.get(associationKey);
          const isLinkedFee = !!this.getProperty(item, '_parentVariantId');
          const isSetupFee = String(item.variant_id) === this.setupFeeProductVariant || !!this.getProperty(item, '_setupFeeForVariant');

          if (isLinkedFee) {
            group.linkedFeeItems.push(item);
            return;
          }

          if (!isSetupFee && !group.parentItem) {
            group.parentItem = item;
          }
        });

        const updates = {};

        groupsByAssociation.forEach(({ parentItem, linkedFeeItems }, associationKey) => {
          if (!linkedFeeItems.length) return;

          if (!parentItem) {
            linkedFeeItems.forEach((linkedFee) => {
              updates[linkedFee.key] = 0;
            });
            this.log('Removing orphaned linked customization fees', { associationKey, count: linkedFeeItems.length });
            return;
          }

          linkedFeeItems.forEach((linkedFee) => {
            if (this.getProperty(linkedFee, '_customizationType') === 'Specialty_Thread') return;
            if (this.getProperty(linkedFee, '_customizationType') === 'Express_Shipping') return;
            if (this.getProperty(linkedFee, '_oneTimeLinkedCharge') === 'true') return;
            if (linkedFee.quantity !== parentItem.quantity) {
              updates[linkedFee.key] = parentItem.quantity;
            }
          });
        });

        if (!Object.keys(updates).length) return;

        this.log('Syncing linked customization fee quantities', updates);
        await this.updateCartItems(updates);
        this.flushPendingRefresh();
      } catch (error) {
        console.error('SNAG Cart: Error syncing linked quantities', error);
      } finally {
        this.isSyncingLinkedQuantities = false;
      }
    },

    async getCart() {
      // Deduplicate concurrent requests
      if (this._cartFetchPromise) {
        return this._cartFetchPromise;
      }

      this._cartFetchPromise = (async () => {
        try {
          const response = await fetch('/cart.js');
          if (!response.ok) {
            this.log('getCart failed with status:', response.status);
            return { items: [] };
          }
          const text = await response.text();
          try {
            return JSON.parse(text);
          } catch (parseError) {
            this.log('getCart JSON parse error, response was:', text.substring(0, 100));
            return { items: [] };
          }
        } catch (error) {
          this.log('getCart network error:', error.message);
          return { items: [] };
        } finally {
          setTimeout(() => { this._cartFetchPromise = null; }, 100);
        }
      })();

      return this._cartFetchPromise;
    },

    getProperty(item, propName) {
      if (!item.properties) return null;
      return item.properties[propName] || null;
    },

    buildAssociationKey(referenceId, parentVariantId) {
      if (!referenceId) return null;
      if (!parentVariantId) return String(referenceId);
      return `${referenceId}::${parentVariantId}`;
    },

    getParentVariantIdForCartItem(item) {
      if (!item) return null;

      const linkedParentVariantId = this.getProperty(item, '_parentVariantId') || this.getProperty(item, '_setupFeeForVariant');
      if (linkedParentVariantId) return String(linkedParentVariantId);
      if (item.variant_id != null) return String(item.variant_id);
      if (item.id != null) return String(item.id);
      return null;
    },

    getAssociationKeyForCartItem(item) {
      const referenceId = this.getProperty(item, '_referenceId');
      const parentVariantId = this.getParentVariantIdForCartItem(item);
      return this.buildAssociationKey(referenceId, parentVariantId);
    },

    getRemovalAssociationKeyForCartItem(item) {
      const associationKey = this.getAssociationKeyForCartItem(item);
      if (associationKey) return associationKey;

      const parentVariantId = this.getParentVariantIdForCartItem(item);
      const isCustomizationLine = !!this.getProperty(item, '_parentVariantId')
        || !!this.getProperty(item, '_setupFeeForVariant')
        || this.hasCustomizationPreviewProperty(item);

      if (!parentVariantId || !isCustomizationLine) return null;
      return `variant::${parentVariantId}`;
    },

    getAssociationKeyForDomItem(cartItem) {
      if (!cartItem) return null;
      const referenceId = cartItem.dataset.referenceId;
      const parentVariantId = cartItem.dataset.parentVariantId || cartItem.dataset.variantId;
      return this.buildAssociationKey(referenceId, parentVariantId);
    },

    hasCustomizationPreviewProperty(item) {
      return [
        '_customizationImageUrl',
        '_customizationFrontImageUrl',
        '_customizationBackImageUrl',
        '_customizationLeftImageUrl',
        '_customizationRightImageUrl',
      ].some((propertyName) => !!this.getProperty(item, propertyName));
    },

    isSnagCustomizationCartItemElement(cartItem) {
      if (!cartItem) return false;

      return !!(
        cartItem.dataset.referenceId
        || cartItem.dataset.parentVariantId
        || cartItem.dataset.snagLinkedItem === 'true'
        || cartItem.dataset.setupFeeForVariant
        || cartItem.dataset.customizationImageUrl
        || cartItem.dataset.customizationFrontImageUrl
        || cartItem.dataset.customizationBackImageUrl
        || cartItem.dataset.customizationLeftImageUrl
        || cartItem.dataset.customizationRightImageUrl
      );
    },

    groupLinkedCartItems() {
      const itemsHolders = document.querySelectorAll('[data-items-holder]');
      if (!itemsHolders.length) return;

      itemsHolders.forEach((itemsHolder) => {
        const cartItems = Array.from(itemsHolder.querySelectorAll('.cart__item[data-item]'));
        if (!cartItems.length) return;

        const itemsByAssociation = new Map();
        cartItems.forEach((cartItem) => {
          const associationKey = this.getAssociationKeyForDomItem(cartItem);
          if (!associationKey) return;

          if (!itemsByAssociation.has(associationKey)) {
            itemsByAssociation.set(associationKey, []);
          }

          itemsByAssociation.get(associationKey).push(cartItem);
        });

        itemsByAssociation.forEach((itemsForAssociation) => {
          const parentItem = itemsForAssociation.find((cartItem) => cartItem.dataset.snagLinkedItem !== 'true');
          if (!parentItem) return;
          const fallbackImageItem = itemsForAssociation.find((cartItem) => cartItem.dataset.customizationImageUrl);
          const customizationImageUrl = parentItem.dataset.customizationImageUrl || fallbackImageItem?.dataset.customizationImageUrl || '';

          const linkedItems = itemsForAssociation.filter(
            (cartItem) => cartItem !== parentItem && cartItem.dataset.snagLinkedItem === 'true'
          );
          if (!linkedItems.length) return;

          parentItem.classList.add('cart__item--snag-parent');

          let linkedItemsContainer = parentItem.querySelector('.cart__linked-items');
          if (!linkedItemsContainer) {
            linkedItemsContainer = document.createElement('div');
            linkedItemsContainer.className = 'cart__linked-items';

            const linkedItemsLabel = document.createElement('p');
            linkedItemsLabel.className = 'cart__linked-items-label';
            linkedItemsLabel.textContent = 'Customization fees';
            linkedItemsContainer.appendChild(linkedItemsLabel);

            parentItem.appendChild(linkedItemsContainer);
          }

          linkedItems.forEach((linkedItem) => {
            linkedItem.classList.add('cart__item--snag-linked');
            this.attachCustomizationViewAction(linkedItem, customizationImageUrl);

            if (linkedItem.parentElement !== linkedItemsContainer) {
              linkedItemsContainer.appendChild(linkedItem);
            }
          });

          this.enhanceSnagCartParent(parentItem, linkedItems, customizationImageUrl);
        });
      });
    },

    syncCartDrawerCountLabel() {
      document.querySelectorAll('[data-snag-cart-count-label]').forEach((label) => {
        const heading = label.closest('.drawer__heading');
        const countEl = heading?.querySelector('[data-cart-count]');
        const count = parseInt(countEl?.getAttribute('data-cart-count') || countEl?.textContent || '0', 10) || 0;
        label.textContent = count === 1 ? ' ITEM' : ' ITEMS';
      });
    },

    enhanceSnagCartParent(parentItem, linkedItems, customizationImageUrl = '') {
      if (!parentItem || !linkedItems?.length) return;

      this.ensureSnagCartActionRow(parentItem, customizationImageUrl);
      this.updateCustomizationFeesSummary(parentItem, linkedItems);
    },

    ensureSnagCartActionRow(parentItem, customizationImageUrl = '') {
      const content = parentItem.querySelector(':scope > .cart__item__content');
      if (!content) return;

      let actionRow = parentItem.querySelector(':scope > .snag-cart-item-actions');
      if (!actionRow) {
        actionRow = document.createElement('div');
        actionRow.className = 'snag-cart-item-actions';
        parentItem.insertBefore(actionRow, parentItem.querySelector('[data-snag-quantity-breaks]') || parentItem.querySelector('.cart__linked-items') || null);
      }

      let detailsButton = actionRow.querySelector('[data-snag-design-details-toggle]');
      if (!detailsButton) {
        detailsButton = document.createElement('button');
        detailsButton.type = 'button';
        detailsButton.className = 'snag-cart-item-actions__details';
        detailsButton.dataset.snagDesignDetailsToggle = 'true';
        detailsButton.innerHTML = '<span>Show design details</span><svg aria-hidden="true" viewBox="0 0 20 20"><path d="M5.5 7.5 10 12l4.5-4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        actionRow.appendChild(detailsButton);
      }

      const customizationViews = this.getCustomizationViewsForItem(parentItem, customizationImageUrl);
      detailsButton.disabled = customizationViews.length === 0;
      detailsButton.setAttribute('aria-expanded', parentItem.classList.contains('is-design-details-expanded') ? 'true' : 'false');

      let links = actionRow.querySelector('.snag-cart-item-actions__links');
      if (!links) {
        links = document.createElement('div');
        links.className = 'snag-cart-item-actions__links';
        actionRow.appendChild(links);
      }

      let editLink = links.querySelector('[data-snag-cart-edit]');
      if (!editLink) {
        editLink = document.createElement('a');
        editLink.className = 'snag-cart-item-actions__edit';
        editLink.dataset.snagCartEdit = 'true';
        editLink.textContent = 'Edit';
        links.appendChild(editLink);
      }

      const productLink = parentItem.querySelector('.cart__item__title a, .cart__item__image a');
      if (productLink?.href) {
        try {
          const editUrl = new URL(productLink.href, window.location.origin);
          editUrl.searchParams.set('customizer', 'true');
          editLink.href = editUrl.toString();
        } catch (error) {
          editLink.href = productLink.href;
        }
      }

      const removeLink = content.querySelector(':scope .cart__quantity-wrapper [data-item-remove]')
        || parentItem.querySelector(':scope > .cart__item__content [data-item-remove]');
      if (removeLink && !links.contains(removeLink)) {
        removeLink.textContent = 'Remove';
        links.appendChild(removeLink);
      }
    },

    getCustomizationViewsForItem(cartItem, fallbackImageUrl = '') {
      if (!cartItem) return [];

      const configuredViews = [
        ['Front', cartItem.dataset.customizationFrontImageUrl],
        ['Back', cartItem.dataset.customizationBackImageUrl],
        ['Left', cartItem.dataset.customizationLeftImageUrl],
        ['Right', cartItem.dataset.customizationRightImageUrl],
      ];
      const views = configuredViews
        .filter(([, url]) => !!url)
        .map(([label, url]) => ({ label, url }));

      if (!views.length && fallbackImageUrl) {
        views.push({ label: 'Design', url: fallbackImageUrl });
      }

      return views;
    },

    toggleInlineDesignDetails(toggle) {
      const parentItem = toggle.closest('.cart__item--snag-parent');
      if (!parentItem) return;

      const views = this.getCustomizationViewsForItem(parentItem, parentItem.dataset.customizationImageUrl || toggle.dataset.imageUrl || '');
      if (!views.length) return;

      const expanded = !parentItem.classList.contains('is-design-details-expanded');
      parentItem.classList.toggle('is-design-details-expanded', expanded);
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.renderInlineDesignDetails(parentItem, views);
    },

    renderInlineDesignDetails(parentItem, views) {
      let details = parentItem.querySelector(':scope > .snag-design-details');
      if (!details) {
        details = document.createElement('div');
        details.className = 'snag-design-details';
        const actionRow = parentItem.querySelector(':scope > .snag-cart-item-actions');
        if (actionRow) {
          actionRow.insertAdjacentElement('afterend', details);
        } else {
          parentItem.appendChild(details);
        }
      }

      const renderSignature = views.map((view) => `${view.label}:${view.url}`).join('|');
      if (details.dataset.renderSignature === renderSignature) return;

      details.dataset.renderSignature = renderSignature;
      details.innerHTML = views.map((view) => `
        <figure class="snag-design-details__item">
          <div class="snag-design-details__thumb">
            <img src="${this.escapeHtml(view.url)}" alt="${this.escapeHtml(view.label)} customization preview" loading="lazy">
          </div>
          <figcaption>${this.escapeHtml(view.label)}</figcaption>
        </figure>
      `).join('');
    },

    updateCustomizationFeesSummary(parentItem, linkedItems) {
      const container = parentItem.querySelector(':scope > .cart__linked-items');
      if (!container) return;

      const totalCents = linkedItems.reduce((sum, linkedItem) => {
        const quantity = this.getCartItemQuantity(linkedItem);
        const unitCents = this.parseCents(linkedItem.dataset.lineFinalUnitPriceCents) || 0;
        return sum + (unitCents * quantity);
      }, 0);
      const total = window.theme?.formatMoney
        ? window.theme.formatMoney(totalCents, theme.moneyFormat)
        : `$${(totalCents / 100).toFixed(2)}`;
      const signature = `${totalCents}:${linkedItems.length}`;
      const wasExpanded = container.dataset.expanded === 'true' || container.classList.contains('is-expanded');

      let label = container.querySelector(':scope > .cart__linked-items-label');
      if (!label) {
        label = document.createElement('div');
        label.className = 'cart__linked-items-label';
        container.insertBefore(label, container.firstChild);
      }

      if (label.dataset.signature !== signature) {
        label.dataset.signature = signature;
        label.innerHTML = `
          <span>CUSTOMIZATION FEES: ${this.escapeHtml(total)}</span>
          <button type="button" class="snag-fees-toggle" data-snag-fees-toggle aria-expanded="${wasExpanded ? 'true' : 'false'}">
            <span data-snag-fees-toggle-label>${wasExpanded ? 'Hide fees' : 'Show fees details'}</span>
            <svg aria-hidden="true" viewBox="0 0 20 20"><path d="M5.5 7.5 10 12l4.5-4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        `;
      } else {
        const toggle = label.querySelector('[data-snag-fees-toggle]');
        const toggleLabel = label.querySelector('[data-snag-fees-toggle-label]');
        if (toggle) toggle.setAttribute('aria-expanded', wasExpanded ? 'true' : 'false');
        if (toggleLabel) toggleLabel.textContent = wasExpanded ? 'Hide fees' : 'Show fees details';
      }

      container.classList.toggle('is-expanded', wasExpanded);
      container.dataset.expanded = wasExpanded ? 'true' : 'false';
    },

    renderCustomizerQuantityBreaks() {
      const parentItems = document.querySelectorAll(
        [
          '[data-cart-page] .cart__item[data-item][data-reference-id][data-snag-linked-item="false"]',
          '[data-cart-drawer] .cart__item[data-item][data-reference-id][data-snag-linked-item="false"]',
          '.drawer--cart .cart__item[data-item][data-reference-id][data-snag-linked-item="false"]',
        ].join(', ')
      );

      parentItems.forEach((parentItem) => {
        const associationKey = this.getAssociationKeyForDomItem(parentItem);
        if (!associationKey) return;

        const quantityInput = parentItem.querySelector(':scope > .cart__item__content .cart__quantity-field');
        const currentQuantity = parseInt(quantityInput?.value || '1', 10) || 1;
        const variantId = parentItem.dataset.variantId || '';
        const referenceId = parentItem.dataset.referenceId || '';
        const requiresSetupFee = parentItem.dataset.snagSetupFeeRequired === 'true';
        const existing = parentItem.querySelector('[data-snag-quantity-breaks]');
        const breakpoints = this.getQuantityBreaksForQuantity(currentQuantity, {
          hiddenBreakpoints: this.getHiddenQuantityBreaksForParentItem(parentItem),
        });
        const estimateSignatureParts = [];

        const cards = breakpoints.map((breakpoint) => {
          const estimate = this.estimateGroupTotalFromDom(parentItem, breakpoint, requiresSetupFee);
          estimateSignatureParts.push(`${breakpoint}:${estimate?.totalCents || ''}:${estimate?.unitCents || ''}`);
          return this.renderQuantityBreakCard({
            breakpoint,
            currentQuantity,
            estimate,
          });
        }).join('');
        const renderSignature = `${associationKey}|${currentQuantity}|${estimateSignatureParts.join(',')}`;

        if (existing?.dataset.renderSignature === renderSignature) return;

        const html = `
          <div
            class="snag-quantity-breaks"
            data-snag-quantity-breaks
            data-render-signature="${this.escapeHtml(renderSignature)}"
            data-reference-id="${this.escapeHtml(referenceId)}"
            data-variant-id="${this.escapeHtml(variantId)}"
            data-association-key="${this.escapeHtml(associationKey)}"
            data-setup-fee-required="${requiresSetupFee ? 'true' : 'false'}"
          >
            <p class="snag-quantity-breaks__title">How many do you need?</p>
            <div class="snag-quantity-breaks__grid">
              ${cards}
            </div>
          </div>
        `;

        if (existing) {
          existing.outerHTML = html;
          return;
        }

        const linkedItemsContainer = parentItem.querySelector('.cart__linked-items');
        if (linkedItemsContainer) {
          linkedItemsContainer.insertAdjacentHTML('beforebegin', html);
        } else {
          parentItem.insertAdjacentHTML('beforeend', html);
        }
      });
    },

    getHiddenQuantityBreaksForParentItem(parentItem) {
      const isCartDrawerItem = !!parentItem.closest('[data-cart-drawer], .drawer--cart');
      return isCartDrawerItem ? this.cartDrawerHiddenQuantityBreaks : [];
    },

    getQuantityBreaksForQuantity(quantity, options = {}) {
      const normalizedQuantity = parseInt(quantity, 10) || 1;
      const hiddenBreakpoints = (options.hiddenBreakpoints || [])
        .map((breakpoint) => parseInt(breakpoint, 10))
        .filter((breakpoint) => Number.isFinite(breakpoint));

      return Array.from(new Set([normalizedQuantity, ...this.quantityBreaks]))
        .filter((breakpoint) => breakpoint >= 1 && !hiddenBreakpoints.includes(breakpoint))
        .sort((left, right) => left - right);
    },

    renderQuantityBreakCard({ breakpoint, currentQuantity, estimate }) {
      const isSelected = breakpoint === currentQuantity;
      const total = estimate?.totalCents != null
        ? window.theme.formatMoney(estimate.totalCents, theme.moneyFormat)
        : '';
      const unit = estimate?.unitCents != null
        ? window.theme.formatMoney(estimate.unitCents, theme.moneyFormat)
        : '';

      return `
        <button
          type="button"
          class="snag-quantity-breaks__card${isSelected ? ' is-selected' : ''}"
          data-snag-quantity-break="${breakpoint}"
          aria-label="Select ${breakpoint} pieces${total ? ` at ${total} total` : ''}"
          ${isSelected ? 'aria-current="true"' : ''}
        >
          ${isSelected ? '<span class="snag-quantity-breaks__badge">Current</span>' : ''}
          <span class="snag-quantity-breaks__qty">${breakpoint}+ pcs</span>
          ${unit ? `<span class="snag-quantity-breaks__unit">${unit} ea</span>` : ''}
          ${total ? `<span class="snag-quantity-breaks__total">${total}</span>` : ''}
          ${total ? '<span class="snag-quantity-breaks__total-label">total</span>' : ''}
        </button>
      `;
    },

    estimateGroupTotalFromDom(parentItem, quantity, requiresSetupFee) {
      const parentVariantId = parentItem.dataset.variantId || '';
      const parentUnitPrice = this.getLineUnitPriceCentsForQuantity(parentItem, quantity);

      if (parentUnitPrice == null) return null;

      let totalCents = Math.round(parentUnitPrice * quantity);
      const linkedItems = Array.from(parentItem.querySelectorAll('.cart__linked-items .cart__item[data-item]'));
      let setupFeeItem = null;

      linkedItems.forEach((linkedItem) => {
        const customizationType = linkedItem.dataset.customizationType || '';
        const isSetupFee = linkedItem.dataset.variantId === this.setupFeeProductVariant || !!linkedItem.dataset.setupFeeForVariant;
        const isOneTimeCharge = customizationType === 'Specialty_Thread' ||
          customizationType === 'Express_Shipping' ||
          linkedItem.dataset.oneTimeLinkedCharge === 'true' ||
          isSetupFee;
        const linkedUnitPrice = this.getLineUnitPriceCentsForQuantity(
          linkedItem,
          isOneTimeCharge ? 1 : quantity,
          parentItem
        );

        if (linkedUnitPrice == null) return;
        if (isSetupFee) setupFeeItem = linkedItem;

        if (isSetupFee && quantity >= this.uploadMinimum) return;
        if (isOneTimeCharge) {
          totalCents += linkedUnitPrice;
          return;
        }

        totalCents += Math.round(linkedUnitPrice * quantity);
      });

      if (!setupFeeItem && parentVariantId) {
        setupFeeItem = Array.from(
          document.querySelectorAll(
            [
              '[data-cart-page] .cart__item[data-setup-fee-for-variant]',
              '[data-cart-drawer] .cart__item[data-setup-fee-for-variant]',
              '.drawer--cart .cart__item[data-setup-fee-for-variant]',
            ].join(', ')
          )
        ).find((item) => item.dataset.setupFeeForVariant === parentVariantId);

        if (setupFeeItem && quantity < this.uploadMinimum) {
          const setupFeeUnitPrice = this.getLineUnitPriceCentsForQuantity(setupFeeItem, 1, parentItem);
          if (setupFeeUnitPrice != null) totalCents += setupFeeUnitPrice;
        }
      }

      if (requiresSetupFee && quantity < this.uploadMinimum && !setupFeeItem) {
        return null;
      }

      return {
        totalCents,
        unitCents: Math.round(totalCents / quantity),
      };
    },

    getLineUnitPriceCentsForQuantity(cartItem, quantity, parentItem = null) {
      const targetQuantity = parseInt(quantity, 10) || 1;
      const estimatedCustomizationPrice = this.getEstimatedCustomizationUnitPriceCents(
        cartItem,
        targetQuantity,
        parentItem
      );
      if (estimatedCustomizationPrice != null) return estimatedCustomizationPrice;

      const currentQuantity = this.getCartItemQuantity(cartItem);
      const currentFinalUnitPrice = this.parseCents(cartItem.dataset.lineFinalUnitPriceCents);

      if (targetQuantity === currentQuantity && currentFinalUnitPrice != null) {
        return currentFinalUnitPrice;
      }

      const priceBreaks = this.getQuantityPriceBreaksForDomItem(cartItem);
      const matchedBreak = priceBreaks
        .filter((priceBreak) => targetQuantity >= priceBreak.minimumQuantity)
        .sort((left, right) => right.minimumQuantity - left.minimumQuantity)[0];

      if (matchedBreak?.priceCents != null) return matchedBreak.priceCents;

      return this.parseCents(cartItem.dataset.lineOriginalUnitPriceCents);
    },

    getEstimatedCustomizationUnitPriceCents(cartItem, quantity, parentItem = null) {
      if (!this.isEmbroideryCustomizationLine(cartItem)) return null;

      return this.getEmbroideryTargetUnitPriceCents(cartItem, quantity, parentItem);
    },

    isEmbroideryCustomizationLine(cartItem) {
      const customizationType = cartItem.dataset.customizationType || '';
      const deliveryMethod = String(cartItem.dataset.deliveryMethod || 'embroidery')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_');

      return this.embroideryCustomizationTypes.includes(customizationType)
        && deliveryMethod === 'embroidery';
    },

    getEmbroideryTargetUnitPriceCents(cartItem, quantity, parentItem = null) {
      const targetPrices = this.getEmbroideryTargetPricesForLine(cartItem, parentItem);
      if (!targetPrices) return null;

      const targetQuantity = parseInt(quantity, 10) || 1;
      const matchedBreakpoint = Object.keys(targetPrices)
        .map((breakpoint) => parseInt(breakpoint, 10))
        .filter((breakpoint) => targetQuantity >= breakpoint)
        .sort((left, right) => right - left)[0];

      if (!matchedBreakpoint) return this.parseCents(cartItem.dataset.lineOriginalUnitPriceCents);

      return targetPrices[matchedBreakpoint];
    },

    getEmbroideryTargetPricesForLine(cartItem, parentItem = null) {
      const variantId = String(cartItem.dataset.variantId || '').trim();
      const productMatchedPrices = this.getProductScopedQuantityPrices(parentItem, variantId);
      if (productMatchedPrices) return productMatchedPrices;

      if (this.customizationQuantityPricingByVariantId[variantId]) {
        return this.customizationQuantityPricingByVariantId[variantId];
      }

      const variantMatchedGroup = Object.values(this.fallbackEmbroideryQuantityTargetPrices)
        .find((group) => group.variantIds.includes(variantId));

      if (variantMatchedGroup) return variantMatchedGroup.pricesByBreakpoint;

      const normalizedView = String(cartItem.dataset.customizationView || '').trim().toLowerCase();
      const viewMatchedGroup = normalizedView === 'front'
        ? this.fallbackEmbroideryQuantityTargetPrices.front
        : ['back', 'left', 'right'].includes(normalizedView)
          ? this.fallbackEmbroideryQuantityTargetPrices.sideBack
          : null;

      return viewMatchedGroup?.pricesByBreakpoint || null;
    },

    getProductScopedQuantityPrices(parentItem, variantId) {
      if (!parentItem || !variantId) return null;

      const productKeys = [
        parentItem.dataset.productId,
        parentItem.dataset.productHandle,
      ].filter(Boolean).map(String);

      for (const productKey of productKeys) {
        const productConfig = this.customizationQuantityPricingByProductKey[productKey];
        if (productConfig?.[variantId]) return productConfig[variantId];
      }

      return null;
    },

    getQuantityPriceBreaksForDomItem(cartItem) {
      try {
        const priceBreaks = JSON.parse(cartItem.dataset.quantityPriceBreaks || '[]');
        if (!Array.isArray(priceBreaks)) return [];

        return priceBreaks
          .map((priceBreak) => ({
            minimumQuantity: parseInt(priceBreak.minimum_quantity, 10),
            priceCents: this.parseCents(priceBreak.price_cents),
          }))
          .filter((priceBreak) => (
            Number.isFinite(priceBreak.minimumQuantity)
            && priceBreak.minimumQuantity > 0
            && priceBreak.priceCents != null
          ));
      } catch (error) {
        return [];
      }
    },

    getCartItemQuantity(cartItem) {
      return parseInt(
        cartItem.querySelector(':scope > .cart__item__content .cart__quantity-field')?.value
          || cartItem.dataset.lineQuantity
          || '1',
        10
      ) || 1;
    },

    parseCents(value) {
      if (value == null || value === '') return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.round(parsed) : null;
    },

    parseMoneyToCents(value) {
      if (!value) return null;
      const normalized = String(value).replace(/[^0-9.-]/g, '');
      if (!normalized) return null;
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
    },

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    async handleQuantityBreakSelection(button) {
      const quantity = parseInt(button.dataset.snagQuantityBreak, 10);
      const container = button.closest('[data-snag-quantity-breaks]');
      const associationKey = container?.dataset.associationKey;
      if (!quantity || !associationKey) return;

      if (this.isApplyingQuantityBreak) return;

      this.isApplyingQuantityBreak = true;
      container.classList.add('is-loading');

      try {
        const cart = await this.getCart();
        const updates = {};
        let referenceId = container.dataset.referenceId || '';
        let parentVariantId = container.dataset.variantId || '';
        let setupFeeItem = null;
        const hasSetupFeeRequirement = container.dataset.setupFeeRequired === 'true';

        cart.items.forEach((item) => {
          const isSetupFee = String(item.variant_id) === this.setupFeeProductVariant || !!this.getProperty(item, '_setupFeeForVariant');
          const setupFeeForVariant = this.getProperty(item, '_setupFeeForVariant');
          const belongsToAssociation = this.getAssociationKeyForCartItem(item) === associationKey;
          const belongsToSetupFeeVariant = isSetupFee && parentVariantId && String(setupFeeForVariant) === String(parentVariantId);
          if (!belongsToAssociation && !belongsToSetupFeeVariant) return;

          const customizationType = this.getProperty(item, '_customizationType') || '';
          const isLinked = !!this.getProperty(item, '_parentVariantId');
          referenceId = referenceId || this.getProperty(item, '_referenceId') || '';
          parentVariantId = parentVariantId || this.getParentVariantIdForCartItem(item) || '';

          if (isSetupFee) {
            setupFeeItem = item;
            updates[item.key] = quantity >= this.uploadMinimum ? 0 : 1;
            return;
          }

          if (isLinked && customizationType === 'Specialty_Thread') {
            updates[item.key] = 1;
            return;
          }

          if (isLinked && (customizationType === 'Express_Shipping' || this.getProperty(item, '_oneTimeLinkedCharge') === 'true')) {
            updates[item.key] = 1;
            return;
          }

          updates[item.key] = quantity;
        });

        await this.updateCartItems(updates);

        if (hasSetupFeeRequirement && quantity < this.uploadMinimum && !setupFeeItem && parentVariantId) {
          await this.addSetupFeeProduct(parentVariantId, referenceId);
        }

        this.flushPendingRefresh();
      } catch (error) {
        console.error('SNAG Cart: Error applying quantity break', error);
      } finally {
        this.isApplyingQuantityBreak = false;
        container.classList.remove('is-loading');
      }
    },

    attachCustomizationViewAction(linkedItem, customizationImageUrl) {
      const linkedRemoveContainer = linkedItem.querySelector('.cart__linked-remove');
      if (!linkedRemoveContainer) return;
      const removeItemLink = linkedRemoveContainer.querySelector('[data-item-remove]');

      let viewCustomizationBtn = linkedRemoveContainer.querySelector('[data-view-customization]');

      if (!customizationImageUrl) {
        if (viewCustomizationBtn) {
          viewCustomizationBtn.remove();
        }
        return;
      }

      if (!viewCustomizationBtn) {
        viewCustomizationBtn = document.createElement('button');
        viewCustomizationBtn.type = 'button';
        viewCustomizationBtn.className = 'cart__item__remove cart__item__view-customization';
        viewCustomizationBtn.dataset.viewCustomization = 'true';
        viewCustomizationBtn.textContent = 'View customization';
      }

      if (removeItemLink) {
        linkedRemoveContainer.insertBefore(viewCustomizationBtn, removeItemLink);
      } else if (viewCustomizationBtn.parentElement !== linkedRemoveContainer) {
        linkedRemoveContainer.appendChild(viewCustomizationBtn);
      }

      viewCustomizationBtn.dataset.imageUrl = customizationImageUrl;
    },

    initCustomizationPreviewModal() {
      let previewModal = document.querySelector('[data-snag-customization-modal]');
      if (!previewModal) {
        previewModal = document.createElement('div');
        previewModal.className = 'snag-customization-modal';
        previewModal.setAttribute('data-snag-customization-modal', '');
        previewModal.setAttribute('aria-hidden', 'true');
        previewModal.innerHTML = `
          <div class="snag-customization-modal__backdrop" data-snag-customization-close></div>
          <div class="snag-customization-modal__dialog" role="dialog" aria-modal="true" aria-label="Customization preview">
            <button type="button" class="snag-customization-modal__close" data-snag-customization-close aria-label="Close customization preview">&times;</button>
            <img class="snag-customization-modal__image" data-snag-customization-image alt="Customization preview">
          </div>
        `;
        document.body.appendChild(previewModal);
      }

      this.customizationPreviewModal = previewModal;
    },

    openCustomizationPreview(imageUrl) {
      if (!imageUrl) return;
      if (!this.customizationPreviewModal) {
        this.initCustomizationPreviewModal();
      }

      const previewImage = this.customizationPreviewModal.querySelector('[data-snag-customization-image]');
      if (!previewImage) return;

      previewImage.src = imageUrl;
      this.customizationPreviewModal.classList.add('is-open');
      this.customizationPreviewModal.setAttribute('aria-hidden', 'false');
    },

    closeCustomizationPreview() {
      if (!this.customizationPreviewModal || !this.customizationPreviewModal.classList.contains('is-open')) return;

      const previewImage = this.customizationPreviewModal.querySelector('[data-snag-customization-image]');
      if (previewImage) {
        previewImage.removeAttribute('src');
      }
      this.customizationPreviewModal.classList.remove('is-open');
      this.customizationPreviewModal.setAttribute('aria-hidden', 'true');
    },

    isCheckoutBlocked() {
      const progressContainer = document.querySelector('[data-snag-progress]');
      if (!progressContainer) return false;
      return progressContainer.dataset.uploadBlocked === 'true';
    },

    checkCheckoutStatus() {
      const blocked = this.isCheckoutBlocked();

      // Skip if state hasn't changed to avoid unnecessary DOM mutations
      if (this._lastBlockedState === blocked) return;
      this._lastBlockedState = blocked;

      const checkoutBtns = document.querySelectorAll('button[name="checkout"], [data-cart-checkout-button] button');

      checkoutBtns.forEach(btn => {
        if (blocked) {
          btn.setAttribute('disabled', 'disabled');
          btn.style.opacity = '0.5';
          btn.style.cursor = 'not-allowed';
          btn.style.pointerEvents = 'none';
        } else {
          btn.removeAttribute('disabled');
          btn.style.opacity = '';
          btn.style.cursor = '';
          btn.style.pointerEvents = '';
        }
      });
    },

    showUploadWarning() {
      const warning = document.querySelector('.snag-upload-warning');
      if (warning) {
        warning.style.animation = 'snag-shake 0.5s ease-in-out';
        warning.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          warning.style.animation = '';
        }, 500);
      }
    },

    onCartChange() {
      // Reset blocked state cache so checkCheckoutStatus re-evaluates
      this._lastBlockedState = undefined;

      if (this.isProcessingSetupFees) {
        this.log('onCartChange: Deferred - setup fees in progress');
        // Don't trigger manageSetupFees to avoid re-entrancy, but queue a
        // deferred re-check so the event isn't lost entirely
        if (!this._deferredCartChange) {
          this._deferredCartChange = setTimeout(() => {
            this._deferredCartChange = null;
            this._lastBlockedState = undefined;
            this.checkCheckoutStatus();
            this.syncCartDrawerCountLabel();
            this.groupLinkedCartItems();
            this.renderCustomizerQuantityBreaks();
            this.debouncedSyncLinkedQuantities();
            this.debouncedManageSetupFees();
          }, 3000);
        }
        return;
      }

      this.syncCartDrawerCountLabel();
      this.groupLinkedCartItems();
      this.renderCustomizerQuantityBreaks();
      this.debouncedSyncLinkedQuantities();

      // Single delayed check instead of 3 staggered timeouts
      setTimeout(() => this.checkCheckoutStatus(), 500);
      // Manage setup fees after cart changes (debounced)
      this.debouncedManageSetupFees();
    },

    debouncedManageSetupFees() {
      // Clear any pending call
      if (this.setupFeeTimeout) {
        clearTimeout(this.setupFeeTimeout);
      }
      // Schedule new call with longer delay to let customizer cart add complete first
      // The customizer adds setup fees directly, so this is mainly for cleanup/sync
      this.setupFeeTimeout = setTimeout(() => {
        this.manageSetupFees();
      }, 2000);
    },

    async manageSetupFees() {
      // Prevent concurrent processing
      if (this.isProcessingSetupFees) {
        this.log('manageSetupFees: Skipped - already processing');
        return;
      }
      this.isProcessingSetupFees = true;
      this.isRefreshPending = false;
      this.log('manageSetupFees: Starting...');

      try {
        const cart = await this.getCart();
        this.log('Cart items:', cart.items.map(i => ({
          variant_id: i.variant_id,
          title: i.title,
          properties: i.properties
        })));

        // Get variant IDs currently in cart (excluding setup fees and customization charges)
        const cartVariantIds = cart.items
          .filter(item => {
            const isSetupFee = String(item.variant_id) === this.setupFeeProductVariant;
            const isCustomization = this.getProperty(item, '_parentVariantId');
            return !isSetupFee && !isCustomization;
          })
          .map(item => String(item.variant_id));
        this.log('Cart variant IDs (excluding setup fees):', cartVariantIds);

        const referenceIdByVariantId = new Map();
        cart.items.forEach((item) => {
          const isSetupFee = String(item.variant_id) === this.setupFeeProductVariant;
          const isCustomization = this.getProperty(item, '_parentVariantId');
          const referenceId = this.getProperty(item, '_referenceId');
          if (!isSetupFee && !isCustomization && referenceId && item.variant_id != null) {
            referenceIdByVariantId.set(String(item.variant_id), referenceId);
          }
        });

        // Get DOM data for setup fee requirements (Liquid has access to product tags)
        const progressContainer = document.querySelector('[data-snag-progress]');
        this.log('Progress container found:', !!progressContainer);

        let setupFeeVariantIdsFromDOM = [];
        let qualifyingQuantity = 0;

        if (progressContainer) {
          this.log('Progress container dataset:', {
            setupFeeVariantIds: progressContainer.dataset.setupFeeVariantIds,
            qualifyingQuantity: progressContainer.dataset.qualifyingQuantity,
            uploadBlocked: progressContainer.dataset.uploadBlocked,
            uploadMinimum: progressContainer.dataset.uploadMinimum
          });

          const setupFeeVariantIdsStr = progressContainer.dataset.setupFeeVariantIds || '';
          setupFeeVariantIdsFromDOM = setupFeeVariantIdsStr
            .split(',')
            .filter(id => id.trim() !== '')
            .map(id => id.trim());
          qualifyingQuantity = parseInt(progressContainer.dataset.qualifyingQuantity, 10) || 0;
        } else {
          this.log('No progress container - checking if setup fees need removal');
        }

        this.log('Setup fee variant IDs from DOM:', setupFeeVariantIdsFromDOM);
        this.log('Qualifying quantity:', qualifyingQuantity);

        // CRITICAL: Cross-reference DOM data with actual cart contents
        // Only consider variants that are BOTH in DOM list AND still in cart
        // This handles the stale DOM issue when variants are removed
        const setupFeeVariantIds = setupFeeVariantIdsFromDOM.filter(
          variantId => cartVariantIds.includes(variantId)
        );
        this.log('Setup fee variant IDs (after cart cross-reference):', setupFeeVariantIds);

        // Find existing setup fee items in cart
        const existingSetupFees = cart.items.filter(item =>
          String(item.variant_id) === this.setupFeeProductVariant
        );
        this.log('Existing setup fees in cart:', existingSetupFees.length);

        // Get the variant IDs that each setup fee is linked to (via _setupFeeForVariant property)
        const existingSetupFeeForVariants = existingSetupFees.map(item =>
          this.getProperty(item, '_setupFeeForVariant')
        ).filter(Boolean);
        this.log('Existing setup fees linked to variants:', existingSetupFeeForVariants);

        // Recalculate qualifying quantity from cart if DOM seems stale
        if (setupFeeVariantIds.length < setupFeeVariantIdsFromDOM.length) {
          this.log('DOM appears stale, some variants no longer in cart');
        }

        // If no variants need setup fees OR qualifying quantity >= minimum, remove ALL setup fees
        if (setupFeeVariantIds.length === 0 || qualifyingQuantity >= this.uploadMinimum) {
          this.log('No setup fees needed:', {
            reason: setupFeeVariantIds.length === 0 ? 'no qualifying variants' : 'minimum reached',
            setupFeeVariantIds,
            qualifyingQuantity,
            uploadMinimum: this.uploadMinimum
          });
          if (existingSetupFees.length > 0) {
            this.log('Removing all existing setup fees');
            const updates = {};
            existingSetupFees.forEach(item => {
              updates[item.key] = 0;
            });
            await this.updateCartItems(updates);
          }
          return;
        }

        // Determine which setup fees to add and which to remove
        const variantsNeedingSetupFee = setupFeeVariantIds.filter(
          variantId => !existingSetupFeeForVariants.includes(variantId)
        );
        this.log('Variants needing setup fee:', variantsNeedingSetupFee);

        const setupFeesToRemove = existingSetupFees.filter(item => {
          const forVariant = this.getProperty(item, '_setupFeeForVariant');
          return forVariant && (!cartVariantIds.includes(forVariant) || !setupFeeVariantIds.includes(forVariant));
        });
        this.log('Setup fees to remove:', setupFeesToRemove.map(i => i.key));

        // Remove setup fees that are no longer needed
        if (setupFeesToRemove.length > 0) {
          this.log('Removing orphaned setup fees...');
          const updates = {};
          setupFeesToRemove.forEach(item => {
            updates[item.key] = 0;
          });
          await this.updateCartItems(updates);
        }

        // Add setup fees for variants that need them
        for (const variantId of variantsNeedingSetupFee) {
          this.log('Adding setup fee for variant:', variantId);
          await this.addSetupFeeProduct(variantId, referenceIdByVariantId.get(String(variantId)) || '');
        }

        this.log('manageSetupFees: Complete');

      } catch (error) {
        console.error('SNAG Cart: Error managing setup fees', error);
      } finally {
        // Reset flag BEFORE dispatching refresh so onCartChange() doesn't drop the event
        this.isProcessingSetupFees = false;
        // Dispatch a single cart refresh after all setup fee operations complete
        this.flushPendingRefresh();
      }
    },

    async addSetupFeeProduct(forVariantId, referenceId = '') {
      this.log('addSetupFeeProduct called with:', {
        forVariantId,
        setupFeeProductVariant: this.setupFeeProductVariant
      });

      try {
        const payload = {
          id: this.setupFeeProductVariant,
          quantity: 1,
          properties: {
            '_setupFeeForVariant': forVariantId,
            ...(referenceId ? { '_referenceId': referenceId } : {}),
            '_hideInCart': 'false'
          }
        };
        this.log('POST /cart/add.js payload:', payload);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const responseText = await response.text();
        this.log('Response status:', response.status, response.statusText);
        this.log('Response body:', responseText);

        if (response.ok) {
          this.log('Setup fee added successfully for variant', forVariantId);
          this.isRefreshPending = true;
        } else {
          console.error('SNAG Cart: Failed to add setup fee', responseText);
        }
      } catch (error) {
        console.error('SNAG Cart: Error adding setup fee product', error);
      }
    }
  };

  // Add shake animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes snag-shake {
      0%, 100% { transform: translateX(0); }
      10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
      20%, 40%, 60%, 80% { transform: translateX(5px); }
    }

    .snag-customization-modal {
      position: fixed;
      inset: 0;
      display: none;
      z-index: 1000;
    }

    .snag-customization-modal.is-open {
      display: block;
    }

    .snag-customization-modal__backdrop {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.7);
    }

    .snag-customization-modal__dialog {
      position: relative;
      z-index: 2;
      width: min(92vw, 760px);
      max-height: 88vh;
      margin: 5vh auto 0;
      background: #fff;
      border-radius: 8px;
      padding: 14px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .snag-customization-modal__close {
      align-self: flex-end;
      border: 0;
      background: transparent;
      font-size: 30px;
      line-height: 1;
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 0;
      color: #111;
    }

    .snag-customization-modal__image {
      width: 100%;
      max-height: calc(88vh - 56px);
      object-fit: contain;
      display: block;
      border-radius: 4px;
    }

    .snag-cart-drawer__heading {
      align-items: baseline;
      gap: 0;
      white-space: nowrap;
    }

    .snag-cart-drawer__heading .cart__items-count::before,
    .snag-cart-drawer__heading .cart__items-count::after {
      content: none;
    }

    .cart__item--snag-parent {
      align-items: flex-start;
    }

    .cart__item--snag-parent > .cart__item__content {
      width: calc(100% - 90px);
    }

    .snag-cart-item-actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      width: calc(100% - 90px);
      margin: 8px 0 0 20px;
      font-size: var(--font-body-x-small);
      line-height: 1.2;
    }

    .snag-cart-item-actions__details,
    .snag-fees-toggle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      border: 0;
      background: transparent;
      color: inherit;
      padding: 0;
      font: inherit;
      line-height: inherit;
      text-transform: none;
      letter-spacing: 0;
      cursor: pointer;
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .snag-cart-item-actions__details[disabled] {
      opacity: 0.45;
      cursor: default;
      text-decoration: none;
    }

    .snag-cart-item-actions__details svg,
    .snag-fees-toggle svg {
      width: 14px;
      height: 14px;
      flex: 0 0 auto;
      transition: transform 150ms ease;
    }

    .snag-cart-item-actions__details[aria-expanded="true"] svg,
    .snag-fees-toggle[aria-expanded="true"] svg {
      transform: rotate(180deg);
    }

    .snag-cart-item-actions__links {
      display: inline-flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
      margin-left: auto;
    }

    .snag-cart-item-actions__links a,
    .snag-cart-item-actions__edit {
      color: inherit;
      font-size: var(--font-body-x-small);
      line-height: 1.1;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .snag-cart-item-actions__links .cart__item__remove {
      margin: 0;
    }

    .snag-design-details {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
      width: calc(100% - 90px);
      margin: 10px 0 0 20px;
      padding: 10px;
      background: rgba(0, 0, 0, 0.03);
      border-radius: 8px;
    }

    .cart__item--snag-parent:not(.is-design-details-expanded) > .snag-design-details {
      display: none;
    }

    .snag-design-details__item {
      margin: 0;
      min-width: 0;
    }

    .snag-design-details__thumb {
      overflow: hidden;
      aspect-ratio: 1 / 1;
      background: #fff;
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-radius: 6px;
    }

    .snag-design-details__thumb img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .snag-design-details figcaption {
      margin-top: 4px;
      font-size: 10px;
      line-height: 1.15;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      opacity: 0.7;
    }

    .cart__linked-items-label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }

    .cart__linked-items:not(.is-expanded) .cart__item--snag-linked {
      display: none;
    }

    .cart__linked-items.is-expanded .cart__item--snag-linked {
      display: flex;
    }

    .snag-quantity-breaks {
      margin: 12px 0 10px;
      padding: 12px;
      border: 1px solid rgba(0, 0, 0, 0.14);
      border-radius: 8px;
      background: #f7f7f5;
    }

    .snag-quantity-breaks__title {
      margin: 0 0 10px;
      font-size: var(--font-body-small);
      line-height: 1.2;
      font-weight: 700;
      color: #111;
    }

    .snag-quantity-breaks__grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }

    .snag-quantity-breaks__card {
      min-height: 88px;
      padding: 10px;
      border: 1px solid #111;
      border-radius: 8px;
      background: #fff;
      color: #111;
      text-align: left;
      cursor: pointer;
      transition: background-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
    }

    .snag-quantity-breaks__card:hover {
      background: #f2f2f0;
    }

    .snag-quantity-breaks__card:focus-visible {
      outline: 2px solid #111;
      outline-offset: 2px;
    }

    .snag-quantity-breaks__card.is-selected {
      box-shadow: inset 0 0 0 2px #111;
    }

    .snag-quantity-breaks__badge {
      display: inline-block;
      margin-bottom: 6px;
      padding: 2px 5px;
      border-radius: 999px;
      background: #111;
      color: #fff;
      font-size: 9px;
      line-height: 1;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    .snag-quantity-breaks.is-loading {
      opacity: 0.65;
      pointer-events: none;
    }

    .snag-quantity-breaks__qty,
    .snag-quantity-breaks__total,
    .snag-quantity-breaks__total-label,
    .snag-quantity-breaks__unit {
      display: block;
    }

    .snag-quantity-breaks__qty {
      font-size: var(--font-body-x-small);
      line-height: 1.1;
      font-weight: 700;
    }

    .snag-quantity-breaks__total {
      margin-top: 4px;
      font-size: clamp(18px, 4vw, 24px);
      line-height: 1;
      font-weight: 800;
      letter-spacing: 0;
    }

    .snag-quantity-breaks__total-label {
      margin-top: 2px;
      font-size: 10px;
      line-height: 1.1;
      font-weight: 800;
      text-transform: uppercase;
    }

    .snag-quantity-breaks__unit {
      margin-top: 5px;
      font-size: 11px;
      line-height: 1.1;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.68);
    }

    @media (min-width: 750px) {
      [data-cart-page] .snag-quantity-breaks__grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
    }

    @media (max-width: 749px) {
      .snag-cart-item-actions,
      .snag-design-details {
        width: 100%;
        margin-left: 0;
      }

      .snag-cart-item-actions {
        flex-wrap: wrap;
      }

      .snag-cart-item-actions__links {
        flex: 1 1 auto;
      }
    }
  `;
  document.head.appendChild(style);

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => SNAG_CART.init());
  } else {
    SNAG_CART.init();
  }

  // Export for external use
  window.SNAG_CART = SNAG_CART;
})();
